import React, { useState, useCallback, useMemo } from 'react';
import { Check, X, Users, Utensils, MessageCircle, Loader2, Edit2, Save } from 'lucide-react';
import { getToken } from '../api/utils';
import "./RSVPModal.css";

const API_URL = (process.env.REACT_APP_API_URL || "http://localhost:8000");

const saveRsvp = async (data) => {
  const token = getToken();
  const response = await fetch(`${API_URL}/guests/${data.event_guest_id}/mainrsvp`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    let message = "Unable to save RSVP.";
    try {
      const payload = await response.json();
      message = payload?.detail || payload?.message || message;
    } catch (_error) {
      // ignore JSON parse errors
    }
    throw new Error(message);
  }

  const respdata = await response.json();
  return { ...respdata, success: true };
};

// const initialRsvpState = {
//   status: 'pending', // 'attending', 'not_attending', 'pending'
//   adults: 1,
//   kids: 0,
//   allergies: '',
//   notes: '',
//   hasResponded: false,
// };

// Sub-Component: Read-Only Summary Display
const RsvpSummary = ({ rsvp, onEdit }) => {
  const isAttending = rsvp.rsvp_status === 'attending';
  const statusClass = isAttending ? 'rsvp-summary-attending' : 'rsvp-summary-not-attending';

  return (
    <div className="rsvp-card rsvp-summary-card">
      <div className="rsvp-summary-header">
        <h2 className="rsvp-h2">Your Response</h2>
        <button
          onClick={onEdit}
          className="rsvp-edit-btn"
          aria-label="Edit RSVP"
        >
          <Edit2 className="rsvp-icon-sm rsvp-mr-1" />
          Edit
        </button>
      </div>

      <div className={`rsvp-status-badge ${statusClass}`}>
        <p className="rsvp-status-text">
          {isAttending ? (
            <>
              <Check className="rsvp-icon-lg rsvp-mr-2" /> Attending
            </>
          ) : (
            <>
              <X className="rsvp-icon-lg rsvp-mr-2" /> Not Attending
            </>
          )}
        </p>
      </div>

      {isAttending && (
        <div className="rsvp-details-grid">
          <DetailRow icon={Users} label="Adults" value={rsvp.rsvp_adult_count} />
          <DetailRow icon={Users} label="Kids (Under 12)" value={rsvp.rsvp_kids_count} />
          <DetailRow icon={Utensils} label="Food Allergies" value={rsvp.rsvp_allergies || 'None provided'} />
          <DetailRow icon={MessageCircle} label="Notes" value={rsvp.rsvp_message || 'No notes'} />
        </div>
      )}

      {rsvp.lastUpdated && (
        <p className="rsvp-last-updated">
          Last Updated: {new Date(rsvp.lastUpdated).toLocaleDateString()} at{' '}
          {new Date(rsvp.lastUpdated).toLocaleTimeString()}
        </p>
      )}
    </div>
  );
};

// Helper for Detail Row in Summary
const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="rsvp-detail-row">
    <Icon className="rsvp-icon-main rsvp-mr-3 rsvp-flex-shrink-0" />
    <span className="rsvp-detail-label">{label}:</span>
    <span className="rsvp-detail-value">{value}</span>
  </div>
);

// Sub-Component: RSVP Form
const RsvpForm = ({ rsvp, setRsvp, onSubmit, isLoading }) => {
  const isAttending = rsvp.rsvp_status === 'attending';

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setRsvp((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value === "" ? "" : parseInt(value, 10) || 0) : value,
    }));
  };

  const handleStatusChange = (status) => {
    setRsvp((prev) => ({
      ...prev,
      rsvp_status: status,
      rsvp_adult_count:
        status === "attending" && !prev.rsvp_adult_count ? 1 : prev.rsvp_adult_count,
      rsvp_kids_count: status === "attending" && prev.rsvp_kids_count === "" ? 0 : prev.rsvp_kids_count,
    }));
  };

  const isFormValid = useMemo(() => {
    if (rsvp.rsvp_status === 'attending') {
      return rsvp.rsvp_adult_count > 0;
    }
    return rsvp.rsvp_status !== 'pending';
  }, [rsvp.rsvp_status, rsvp.rsvp_adult_count]);

  const attendingClass = isAttending ? 'rsvp-btn-attending-active' : 'rsvp-btn-attending-inactive';
  const notAttendingClass = rsvp.rsvp_status === 'not_attending' ? 'rsvp-btn-not-attending-active' : 'rsvp-btn-not-attending-inactive';

  return (
    <form onSubmit={onSubmit} className="rsvp-card rsvp-form-card">
      <div className="rsvp-form-intro">
        <p className="rsvp-kicker">RSVP</p>
        <h2 className="rsvp-h2">Will you be joining?</h2>
        <p className="rsvp-subtitle">Share your response so the host can plan the celebration beautifully.</p>
      </div>

      {/* RSVP Status Buttons */}
      <div className="rsvp-response-block">
        <div className="rsvp-button-group">
          <button
            type="button"
            onClick={() => handleStatusChange('attending')}
            className={`rsvp-btn-base ${attendingClass}`}
            disabled={isLoading}
          >
            <Check className="rsvp-icon-sm rsvp-mr-2" /> Yes, I will attend
          </button>
          <button
            type="button"
            onClick={() => handleStatusChange('not_attending')}
            className={`rsvp-btn-base ${notAttendingClass}`}
            disabled={isLoading}
          >
            <X className="rsvp-icon-sm rsvp-mr-2" /> No, I cannot make it
          </button>
        </div>
      </div>

      {/* Guest Details (Conditional) */}
      {isAttending && (
        <div className="rsvp-details-section">
          <h3 className="rsvp-h3 rsvp-mb-3">Guest details</h3>
          <div className="rsvp-grid-cols-2 rsvp-gap-4">
            {/* Adults Count */}
            <div>
              <label htmlFor="adults" className="rsvp-label rsvp-mb-1">
                <Users className="rsvp-icon-xs rsvp-mr-1" /> Adults*
              </label>
              <input
                type="number"
                name="rsvp_adult_count"
                id="rsvp_adult_count"
                min="1"
                max="10"
                value={rsvp.rsvp_adult_count}
                onChange={handleChange}
                required
                className="rsvp-input"
              />
            </div>
            {/* Kids Count */}
            <div>
              <label htmlFor="kids" className="rsvp-label rsvp-mb-1">
                <Users className="rsvp-icon-xs rsvp-mr-1" /> Kids
              </label>
              <input
                type="number"
                name="rsvp_kids_count"
                id="rsvp_kids_count"
                min="0"
                max="10"
                value={rsvp.rsvp_kids_count}
                onChange={handleChange}
                className="rsvp-input"
              />
            </div>
          </div>

          {/* Food Allergies */}
          <div>
            <label htmlFor="allergies" className="rsvp-label rsvp-mb-1">
              <Utensils className="rsvp-icon-xs rsvp-mr-1" /> Dietary Restrictions / Allergies
            </label>
            <input
              type="text"
              name="rsvp_allergies"
              id="rsvp_allergies"
              value={rsvp.rsvp_allergies}
              onChange={handleChange}
              placeholder="e.g. gluten-free, peanut allergy"
              className="rsvp-input"
            />
          </div>

          {/* General Notes */}
          <div>
            <label htmlFor="notes" className="rsvp-label rsvp-mb-1">
              <MessageCircle className="rsvp-icon-xs rsvp-mr-1" /> Message for host
            </label>
            <textarea
              name="rsvp_message"
              id="rsvp_message"
              rows="2"
              value={rsvp.rsvp_message}
              onChange={handleChange}
              placeholder="Add a short note if needed"
              className="rsvp-textarea"
            ></textarea>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading || !isFormValid}
        className={`rsvp-submit-btn ${isLoading || !isFormValid ? 'rsvp-submit-disabled' : 'rsvp-submit-enabled'}`}
      >
        {isLoading ? (
          <>
            <Loader2 className="rsvp-icon-sm rsvp-mr-2 rsvp-spin" /> Saving Response...
          </>
        ) : (
          <>
            <Save className="rsvp-icon-sm rsvp-mr-2" /> Save RSVP
          </>
        )}
      </button>

      {!isFormValid && (
        <p className="rsvp-error-message">
          * Please select an attendance status, and ensure you specify at least 1 adult if attending.
        </p>
      )}
    </form>
  );
};

// Main Component
export default function RSVPModal({ guest, onClose, onSuccess }) {
  const [rsvpData, setRsvpData] = useState({
    ...guest,
    rsvp_adult_count: guest.rsvp_adult_count ?? "",
    rsvp_kids_count: guest.rsvp_kids_count ?? "",
    hasResponded: (guest.rsvp_status !== "pending"),
  });
  const [isEditing, setIsEditing] = useState(!guest.rsvp_status || guest.rsvp_status === "pending");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");


  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (rsvpData.rsvp_status === 'pending') {
      console.error('Please select an attendance status.');
      return;
    }

    setIsLoading(true);
    setErrorMessage("");
    try {
      const payload = {
        ...rsvpData,
        rsvp_adult_count:
          rsvpData.rsvp_status === "attending" ? Number(rsvpData.rsvp_adult_count || 1) : null,
        rsvp_kids_count:
          rsvpData.rsvp_status === "attending" ? Number(rsvpData.rsvp_kids_count || 0) : null,
      };
      const response = await saveRsvp(payload);

      if (response.success) {
        setRsvpData({ ...payload, hasResponded: true });
        setIsEditing(false); // Switch to read-only view
        if (onSuccess) {
          onSuccess(response);
        }
        onClose();
      } else {
        setErrorMessage("Unable to save RSVP. Please try again.");
      }
    } catch (error) {
      console.error('Network or API Error:', error);
      setErrorMessage(error?.message || "Unable to save RSVP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [rsvpData, onClose, onSuccess]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  return (
    <div className="rsvp-page-container">
      <div className="rsvp-card-wrapper">
        {/* <h1 className="rsvp-title">Event RSVP</h1> */}

        {/* {isEditing || !rsvpData.hasResponded ? ( */}
        {isEditing  ? (
          <>
            <RsvpForm
              rsvp={rsvpData}
              setRsvp={setRsvpData}
              onSubmit={handleSubmit}
              isLoading={isLoading}
            />
            {errorMessage ? <p className="rsvp-error-message">{errorMessage}</p> : null}
          </>
        ) : (
          <RsvpSummary rsvp={rsvpData} onEdit={handleEdit} />
        )}
      </div>
    </div>
  );
}
